// Updated SessionTimeoutManager.js with fixes for shorter timeouts

import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';

class SessionTimeoutManager {
  constructor() {
    this.timeoutId = null;
    this.timeoutDuration = 60 * 60 * 1000; // 2 minutes default
    this.warningTimeoutId = null;
    this.warningTime = 5 * 60 * 1000; // 1 minute warning
    this.onTimeout = null;
    this.onWarning = null;
    this.lastActivity = Date.now();
    this.isActive = false; // Track if the manager is active
  }

  // Initialize the timeout manager
  init(options = {}) {
    console.log('Initializing SessionTimeoutManager with options:', options);
    
    this.timeoutDuration = options.timeoutDuration || this.timeoutDuration;
    this.warningTime = options.warningTime || this.warningTime;
    this.onTimeout = options.onTimeout || this.defaultTimeout;
    this.onWarning = options.onWarning || this.defaultWarning;
    this.lastActivity = Date.now();
    this.isActive = true;

    // Remove any existing listeners first to avoid duplicates
    this.removeActivityListeners();
    
    // Add event listeners for user activity
    this.setupActivityListeners();
    
    // Start the initial timer
    this.resetTimer();
    
    console.log('SessionTimeoutManager initialized with:', {
      timeoutDuration: this.timeoutDuration / 1000 / 60 + ' minutes',
      warningTime: this.warningTime / 1000 / 60 + ' minutes',
    });
  }

  // Setup activity listeners to detect user interaction
  setupActivityListeners() {
    console.log('Setting up activity listeners');
    
    this.boundResetTimer = this.resetTimer.bind(this);
    
    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 
      'scroll', 'touchstart', 'click', 'keydown'
    ];
    
    // Add all the listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, this.boundResetTimer);
    });
  }
  
  // Remove activity listeners when cleaning up
  removeActivityListeners() {
    console.log('Removing activity listeners');
    
    if (this.boundResetTimer) {
      const activityEvents = [
        'mousedown', 'mousemove', 'keypress', 
        'scroll', 'touchstart', 'click', 'keydown'
      ];
      
      activityEvents.forEach(event => {
        window.removeEventListener(event, this.boundResetTimer);
      });
    }
  }

  // Reset the timer whenever user activity is detected
  resetTimer() {
    if (!this.isActive) return;
    
    console.log('Activity detected, resetting timer');
    this.lastActivity = Date.now();
    
    // Clear existing timeouts
    this.clearTimeouts();
    
    // Set warning timeout if applicable
    if (this.warningTime && this.warningTime < this.timeoutDuration) {
      const warningDelay = this.timeoutDuration - this.warningTime;
      
      console.log(`Setting warning timeout to trigger in ${warningDelay/1000} seconds`);
      
      this.warningTimeoutId = setTimeout(() => {
        console.log('Warning timeout triggered');
        this.handleWarning();
      }, warningDelay);
    }
    
    // Set the main timeout
    console.log(`Setting main timeout to trigger in ${this.timeoutDuration/1000} seconds`);
    this.timeoutId = setTimeout(() => {
      console.log('Main timeout triggered');
      this.handleTimeout();
    }, this.timeoutDuration);
  }

  // Clear all active timeouts
  clearTimeouts() {
    console.log('Clearing existing timeouts');
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  // Handle the warning event
  handleWarning() {
    console.log('Handling warning');
    if (typeof this.onWarning === 'function') {
      this.onWarning(Math.floor(this.warningTime / 1000 / 60));
    }
  }

  // Handle the timeout event
  async handleTimeout() {
    console.log('Handling timeout');
    // Clear timeouts first
    this.clearTimeouts();
    
    if (typeof this.onTimeout === 'function') {
      await this.onTimeout();
    } else {
      await this.defaultTimeout();
    }
  }

  // Default timeout handler - logs the user out
  defaultTimeout = async () => {
    console.log('Executing default timeout (logout)');
    try {
      this.isActive = false; // Deactivate the manager
      await signOut(auth);
      // Redirect to login would happen via auth state change listeners in your app
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  // Default warning handler - logs to console only
  defaultWarning = (minutesLeft) => {
    console.warn(`Session will expire in ${minutesLeft} minutes due to inactivity.`);
  }

  // Get the time remaining in the session
  getTimeRemaining() {
    const elapsed = Date.now() - this.lastActivity;
    const total = Math.max(0, this.timeoutDuration - elapsed);
    const remaining = (elapsed > (this.timeoutDuration - this.warningTime)) 
      ? Math.max(0, this.warningTime - (elapsed - (this.timeoutDuration - this.warningTime)))
      : this.warningTime;
      
    return {
      total: remaining,
      minutes: Math.floor((remaining / 1000 / 60) % 60),
      seconds: Math.floor((remaining / 1000) % 60)
    };
  }
  
  // Check if the session has expired
  isExpired() {
    return this.getTimeRemaining().total <= 0;
  }
  
  // Change the timeout duration
  setTimeoutDuration(durationInMinutes) {
    console.log(`Changing timeout duration to ${durationInMinutes} minutes`);
    this.timeoutDuration = durationInMinutes * 60 * 1000;
    this.resetTimer();
  }
}

// Create and export a singleton instance
const sessionTimeoutManager = new SessionTimeoutManager();
export default sessionTimeoutManager;